import { useSWRFetch } from '@hooks/useSWRFetch';
import { DateRange } from '@components/shared/DateRange/types';
import { RELEASE_DATE_SORT, TEN_MINUTES_IN_MS } from '@helpers/constants';
import { CheckUnclearedByLicensorResult, UnclearedVideosResult } from './types';

export const useShowUnclearedVideos = (
  page: number,
  limit: number,
  licensors: string[],
  dateRange?: DateRange,
  releaseDateSortDirection?: string,
) => {
  const searchParams = new URLSearchParams();
  searchParams.append('pagination.offset', ((page - 1) * limit).toString());
  searchParams.append('pagination.limit', limit.toString());
  for (const licensor of licensors) {
    searchParams.append('licensor_id', `spotify:licensor:${licensor}`);
  }

  if (dateRange) {
    const startDateString = dateRange.startDate?.toISOString();
    const endDateString = dateRange.endDate?.toISOString();
    if (startDateString) {
      searchParams.append('date_filter.start.timestamp', startDateString);
    }
    if (endDateString) {
      searchParams.append('date_filter.end.timestamp', endDateString);
    }
  }

  if (releaseDateSortDirection && releaseDateSortDirection !== 'none') {
    searchParams.append('order_by.type', 'RELEASE_DATE');
    searchParams.append(
      'order_by.direction',
      releaseDateSortDirection === RELEASE_DATE_SORT.ASCENDING ? 'ASC' : 'DESC',
    );
  }

  const requestObject = {
    url: `v1/nonClearedMusicVideosByLicensor?${new URLSearchParams(
      searchParams,
    )}`,
  };

  const canRequest = page >= 0 && limit >= 1;
  const { data, error, isValidating, isLoading, status, mutate } =
    useSWRFetch<UnclearedVideosResult>(canRequest ? requestObject : null, {
      dedupingInterval: TEN_MINUTES_IN_MS,
    });

  return {
    data,
    error,
    isValidating,
    isLoading,
    status,
    mutate,
  };
};

export const useShowUnclearedVideoFlag = (licensors: string[]) => {
  const searchParams = new URLSearchParams();

  for (const licensor of licensors) {
    searchParams.append('licensor_id', licensor);
  }

  const requestObject = {
    url: `v1/checkNonClearedMusicVideosByLicensor?${new URLSearchParams(
      searchParams,
    )}`,
  };

  const { data, status, isLoading } =
    useSWRFetch<CheckUnclearedByLicensorResult>(requestObject, {
      dedupingInterval: TEN_MINUTES_IN_MS,
    });

  if (status === 200) {
    return {
      doShow: data.exists,
      isLoading,
    };
  }

  return { doShow: false, isLoading };
};
